/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS migration — reference-app tiles used by the SME Performance and
   Retention (KAM) pages: KpiCard (accent bar + icon badge + definition tooltip),
   the compact `.tile-3d` Stat / click tile, the window ScopeTag, the tapered
   funnel (sqrt width scale with a floor) and the chart insight panel.
   ──────────────────────────────────────────────────────────────────────────── */
import React from 'react'
import { Box, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DateRangeIcon from '@mui/icons-material/DateRange'
import { GX_BORDER, GX_CARD_SX, GX_INK_500, GX_INK_700, GX_INK_900 } from '../growthos-shared/GrowthOsUi'
import { integer, percentage } from '../growthos-shared/gxFormat'

const INK_400 = '#94a3b8'
const INK_300 = '#cbd5e1'

const KPI_ACCENTS = {
  indigo: 'linear-gradient(90deg,#4F46E5,#06B6D4)',
  teal: 'linear-gradient(90deg,#0D9488,#10B981)',
  amber: 'linear-gradient(90deg,#D97706,#F59E0B)',
  rose: 'linear-gradient(90deg,#E11D48,#F472B6)',
}
const KPI_TINT = {
  indigo: { bg: 'rgba(79,70,229,0.10)', fg: '#4F46E5' },
  teal: { bg: 'rgba(13,148,136,0.10)', fg: '#0D9488' },
  amber: { bg: 'rgba(217,119,6,0.10)', fg: '#D97706' },
  rose: { bg: 'rgba(225,29,72,0.10)', fg: '#E11D48' },
}
export const STAT_ACCENT = {
  indigo: 'linear-gradient(90deg,#4f46e5,#6366f1)',
  teal: 'linear-gradient(90deg,#06b6d4,#22d3ee)',
  emerald: 'linear-gradient(90deg,#059669,#10b981)',
  amber: 'linear-gradient(90deg,#d97706,#f59e0b)',
  rose: 'linear-gradient(90deg,#e11d48,#fb7185)',
  slate: 'linear-gradient(90deg,#475569,#94a3b8)',
}
const TILE_ICON = { indigo: '#6366f1', teal: '#06b6d4', amber: '#f59e0b', rose: '#f43f5e', emerald: '#10b981', slate: GX_INK_500 }

const tileSx = (active) => ({
  ...GX_CARD_SX, border: `1px solid ${GX_BORDER}`, position: 'relative', overflow: 'hidden', height: '100%',
  p: 2, boxSizing: 'border-box',
  transition: 'transform 160ms ease, box-shadow 160ms ease',
  boxShadow: active ? `0 0 0 2px #818cf8, ${GX_CARD_SX.boxShadow}` : GX_CARD_SX.boxShadow,
})

/** Reference KpiCard: 3px accent bar, uppercase label + info tooltip, icon badge, big value, sub. */
export function KpiTile({ label, value, sub, accent = 'indigo', icon, definition, loading, onClick, active }) {
  const tint = KPI_TINT[accent] || KPI_TINT.indigo
  const body = (
    <Box sx={{ ...tileSx(active), p: 2.5 }} title={definition}>
      <Box aria-hidden sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: 3, background: KPI_ACCENTS[accent] }} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_INK_500 }}>{label}</Typography>
          {definition && <InfoOutlinedIcon sx={{ fontSize: 13, color: INK_300, cursor: 'help' }} />}
        </Box>
        {icon && (
          <Box sx={{ width: 32, height: 32, borderRadius: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: tint.bg, color: tint.fg }}>{icon}</Box>
        )}
      </Box>
      {loading ? (
        <Box sx={{ mt: 1.5, height: 32, width: 96, borderRadius: '8px', bgcolor: '#eef0f6' }} />
      ) : (
        <Typography sx={{ fontSize: 30, fontWeight: 700, color: GX_INK_900, mt: 1, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</Typography>
      )}
      {sub && <Typography sx={{ fontSize: 12, color: GX_INK_500, mt: 0.75 }}>{sub}</Typography>}
    </Box>
  )
  if (!onClick) return body
  return (
    <Box component="button" type="button" onClick={onClick} aria-pressed={active}
      sx={{ textAlign: 'left', border: 'none', p: 0, bgcolor: 'transparent', cursor: 'pointer', borderRadius: '16px', font: 'inherit', display: 'block', width: '100%' }}>
      {body}
    </Box>
  )
}

/** Reference `.tile-3d` Stat / ClickTile: accent bar, label (+icon, info, chevron), value, sub. */
export function Stat({ label, value, sub, definition, accent = 'slate', icon, onClick, active, chevron = true }) {
  const body = (
    <Box sx={tileSx(active)} title={definition}>
      <Box aria-hidden sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: 3, background: STAT_ACCENT[accent] }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: GX_INK_500 }}>
        {icon && <Box sx={{ display: 'flex', color: TILE_ICON[accent] || GX_INK_500 }}>{icon}</Box>}
        <span>{label}</span>
        {definition && <InfoOutlinedIcon sx={{ fontSize: 12, color: INK_300, cursor: 'help' }} />}
        {onClick && chevron && <ExpandMoreIcon sx={{ fontSize: 15, ml: 'auto', color: INK_300, transition: 'transform 160ms', transform: active ? 'rotate(180deg)' : 'none' }} />}
      </Box>
      <Typography sx={{ mt: 0.75, fontSize: '1.7rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.01em', color: GX_INK_900, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Typography sx={{ mt: 0.75, fontSize: 12, color: INK_400 }}>{sub}</Typography>}
    </Box>
  )
  if (!onClick) return body
  return (
    <Box component="button" type="button" onClick={onClick} aria-expanded={active}
      sx={{ textAlign: 'left', border: 'none', p: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit', display: 'block', width: '100%', borderRadius: '16px', '&:hover > div': { transform: 'translateY(-1px)' } }}>
      {body}
    </Box>
  )
}

/** Reference ScopeTag (".badge-window"): the window that governs a section. */
export function ScopeTag({ label, title }) {
  return (
    <Box component="span" title={title} sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '9999px',
      border: '1px solid rgba(79,70,229,0.25)', background: 'linear-gradient(90deg, rgba(79,70,229,0.08), rgba(6,182,212,0.08))',
      color: '#4f46e5', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>
      <DateRangeIcon sx={{ fontSize: 12 }} /> {label}
    </Box>
  )
}

/** Reference `.section-title` row: icon + title + optional scope tag. */
export function SectionTitle({ icon, children, tag }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
      {icon && <Box sx={{ display: 'flex', color: '#6366f1' }}>{icon}</Box>}
      <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: GX_INK_700 }}>{children}</Typography>
      {tag}
    </Box>
  )
}

/** Responsive tile grid — `cols` = columns at xl (reference xl:grid-cols-N). */
export function TileGrid({ cols = 4, smCols = 2, xsCols = 1, children, sx }) {
  return (
    <Box sx={{
      display: 'grid', gap: 2,
      gridTemplateColumns: { xs: `repeat(${xsCols}, minmax(0, 1fr))`, sm: `repeat(${smCols}, minmax(0, 1fr))`, lg: `repeat(${cols}, minmax(0, 1fr))` },
      ...sx,
    }}>
      {children}
    </Box>
  )
}

// Reference funnelWidthPct(): sqrt scale + a floor so a long tail stays legible.
export const funnelWidthPct = (pct, floor = 24) => floor + (100 - floor) * Math.sqrt(Math.max(0, Math.min(100, pct ?? 0)) / 100)

export function TaperedFunnel({ stages, minWidthPct }) {
  return (
    <Box sx={{ mx: 'auto', maxWidth: 672, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      {stages.map((s) => (
        <Box key={s.key} sx={{ width: `${funnelWidthPct(s.pct, minWidthPct)}%` }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, color: '#fff', background: s.gradient,
            clipPath: 'polygon(4% 0%, 96% 0%, 100% 100%, 0% 100%)', textShadow: '0 1px 2px rgba(0,0,0,0.25)',
            boxShadow: `0 20px 32px -14px ${s.shadow}, 0 8px 14px -8px ${s.shadow}, inset 0 2px 0 rgba(255,255,255,0.4), inset 0 -4px 8px rgba(0,0,0,0.22)`,
          }}>
            <Box sx={{ display: 'flex', opacity: 0.9, flexShrink: 0 }}>{s.icon}</Box>
            <Box sx={{ display: 'flex', flex: 1, minWidth: 0, alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}>
              <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{s.label}</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
                {integer(s.count)}
                {s.pct != null && <Box component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, opacity: 0.8 }}>({percentage(s.pct)})</Box>}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

/** Animated-free smiley for the chart mood pill (reference MoodFace). */
export function MoodFace({ face, size = 18 }) {
  const mouth = face === 'happy' ? 'M10.5 19 Q16 24.5 21.5 19' : face === 'sad' ? 'M10.5 22 Q16 16.5 21.5 22' : 'M11 20.5 H21'
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="13" stroke="currentColor" strokeWidth="2.2" opacity="0.9" />
      <g fill="currentColor"><circle cx="11.5" cy="13" r="1.7" /><circle cx="20.5" cy="13" r="1.7" /></g>
      <path d={mouth} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

export function InsightPanel({ title, rows, mood }) {
  return (
    <Box sx={{ borderRadius: '12px', border: `1px solid ${GX_BORDER}`, p: 1.75, minWidth: { sm: 240 }, bgcolor: 'rgba(79,70,229,.04)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: GX_INK_500 }}>{title}</Typography>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, borderRadius: '9999px', px: 1, py: 0.25, fontSize: 11, fontWeight: 600, color: mood.color, bgcolor: `${mood.bg}` }}>
          <MoodFace face={mood.face} /> {mood.label}
        </Box>
      </Box>
      <Box sx={{ mt: 1 }}>
        {rows.map((r) => (
          <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, fontSize: 12, py: 0.25 }}>
            <Box component="span" sx={{ color: INK_400 }}>{r.label}</Box>
            <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: r.color || '#1e293b' }}>{r.value}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

/** Card wrapper matching the reference `.card` panels. */
export function Panel({ children, sx }) {
  return <Box sx={{ ...GX_CARD_SX, border: `1px solid ${GX_BORDER}`, overflow: 'hidden', ...sx }}>{children}</Box>
}
